import { computed } from 'vue'
import type { TaxReason } from '@/types'
import { useAccountPricingStore, type AccountPricing } from '@/stores/accountPricing'

export type { AccountPricing }

/**
 * The i18n key saying why a net-basis account pays no German VAT. The net
 * basis applies only for reverse_charge and non_eu, so any other reason reads
 * as non_eu.
 */
export function netBasisNoteKey(reason: TaxReason | null): string {
  return reason === 'reverse_charge' ? 'cart.summary.reverseChargeNote' : 'cart.summary.notTaxableNote'
}

/**
 * The i18n key of the note beside prices shown to the viewer: why a net-basis
 * account pays no German VAT, else whether the prices include it, which an
 * organization on the gross basis is shown without.
 */
export function pricingNoteKey(pricing: AccountPricing, isOrganization: boolean): string {
  if (pricing.basis === 'net') return netBasisNoteKey(pricing.reason)
  return isOrganization ? 'domains.results.pricing.exclVat' : 'domains.results.pricing.inclVat'
}

/**
 * The same note as a sentence of its own, for a note that stands apart from
 * the prices rather than trailing them.
 */
export function pricingNoteSentenceKey(pricing: AccountPricing, isOrganization: boolean): string {
  if (pricing.basis === 'net') return netBasisNoteKey(pricing.reason)
  return isOrganization ? 'cart.summary.pricesExcludeTax' : 'cart.summary.pricesIncludeTax'
}

/**
 * How the signed-in account is priced: gross with German VAT unless the
 * backend says net. Gross is also the answer before the request returns and
 * after it fails, since that is what every account was before the basis.
 */
export function useAccountPricing() {
  const store = useAccountPricingStore()
  const ready = store.load()
  const pricing = computed(() => store.pricing)
  const isNet = computed(() => store.pricing.basis === 'net')

  return { pricing, isNet, ready }
}
